<!-- ProfileView.svelte -->

<script>
  import { onMount, onDestroy } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { fly } from 'svelte/transition';
  import { Browser } from '@capacitor/browser';
  import backarrow from './assets/backarrow.png';
  import { App } from '@capacitor/app';
  import { Capacitor } from '@capacitor/core';
  import { Plugins } from '@capacitor/core';

  const dispatch = createEventDispatcher();

  let profile = null;
  let isLoading = true;
  let showEditNamePopup = false;
  let showAboutUsPopup = false;
  let showTermsPopup = false;  
  let showPrivacyPopup = false; 
  let newName = '';
  let updateMessage = '';
  let isSubmitting = false;
  let activeTab = 'settings';
  let error = null;

  async function fetchProfile() {
    isLoading = true;
    error = null;
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch('https://groupfruits.online/users/api/GetUserProfileView/', {
        headers: {
          'Authorization': `JWT ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      
      // Add null checks for each field
      profile = {
        full_name: data.full_name || 'N/A',
        phone: data.phone || 'N/A',
        delivery_point: data.delivery_point || { name: 'N/A', address: 'N/A', whatsapp_group_link: null }
      };
    } catch (err) {
      error = 'Unable to load profile. Please check your internet connection and try again.';
      console.error('Error fetching profile:', err);
    } finally {
      isLoading = false;
    }
  }

  onMount(() => {
    fetchProfile();
    // Reset scroll position on component mount
    window.scrollTo(0, 0);

    if (Capacitor.isNativePlatform()) {
      App.addListener('backButton', goBack);
    }
  });

  onDestroy(() => {
    if (Capacitor.isNativePlatform()) {
      App.removeAllListeners();
    }
  });
  
  function handleRefresh() {
    fetchProfile();
  }
  
  function goBack() {
    dispatch('goToHome');
  }

  function openEditNamePopup() {
    newName = profile.full_name;
    showEditNamePopup = true;
  }

  function closeEditNamePopup() {
    showEditNamePopup = false;
    updateMessage = '';
  }

  async function updateName() {
    if (newName.length < 3) {
      updateMessage = 'Name must be at least 3 characters long';
      return;
    }

    isSubmitting = true;
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch('https://groupfruits.online/users/api/UpdateUserProfileView/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `JWT ${accessToken}`,
        },
        body: JSON.stringify({ full_name: newName }),
      });

      if (response.ok) {
        const result = await response.json();
        updateMessage = result.message;
        profile.full_name = newName;
        setTimeout(closeEditNamePopup, 2000);
      } else {
        updateMessage = 'Failed to update name';
      }
    } catch (error) {
      console.error('Error updating name:', error);
      updateMessage = 'Error updating name';
    } finally {
      isSubmitting = false;
    }
  }

  function changePickupPoint() {
    dispatch('pleaseChangeDeliveryPoint');
  }

  function logout() {
    dispatch('pleaseLogout');
  }

  function viewSupport() {
    dispatch('pleaseSupport');
  }

  async function openLink(url) {
    try {
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: url });
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Failed to open link:', err);
    }
  }

  // Function to open direct WhatsApp chat with support
  function chatWithSupport() {
    const phoneNumber = "919490028494";
    const whatsappUrl = `https://wa.me/${phoneNumber}`;
    openLink(whatsappUrl);
  }

  // Function to join WhatsApp group
  function joinWhatsAppGroup() {
    const groupLink = profile?.delivery_point?.whatsapp_group_link || "https://chat.whatsapp.com/DhrBrMDaHoL1c5YTJTi3JI";
    openLink(groupLink);
  }
  
  function openAboutUsPopup() {
    showAboutUsPopup = true;
  }

  function closeAboutUsPopup() {
    showAboutUsPopup = false;
  }
  
  function openTermsPopup() {
    showTermsPopup = true;
  }

  function closeTermsPopup() {
    showTermsPopup = false;
  }  

  function openPrivacyPopup() {
    showPrivacyPopup = true;
  }

  function closePrivacyPopup() {
    showPrivacyPopup = false;
  }  
</script>

<style>
  * {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  :global(body) {
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  .bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: #ffffff;
    display: flex;
    justify-content: space-around;
    align-items: center;
    height: 60px;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }

  .profile-view {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
    background-color: #f9f9f9;
    min-height: 100vh;
    padding: 10px 10px 20px 10px;
    padding-top: calc(65px + env(safe-area-inset-top));
    padding-bottom: calc(80px + env(safe-area-inset-bottom));
  }

  .header {
    position: fixed;
    top: env(safe-area-inset-top);
    left: 0;
    right: 0;
    background-color: white;
    padding-top: 13.5px; 
    padding-bottom: 13.5px; 
    padding-left: 16px;
    padding-right: 16px;
    z-index: 10;
    box-sizing: border-box;
    border-bottom: 0.1px solid #e5dada; 
    box-shadow: none; 
    display: flex;
    align-items: center;
  }

  .back-button {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px; 
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  .back-button img {
    width: 100%;
    height: 100%;
    object-fit: contain; 
  }

  .header-title {
    flex-grow: 1;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    padding-right: 28px;
    padding-left: 38px;
  }

  .card {
    background-color: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid #e0e0e0;
  }

  .profile-info {
    display: flex;
    align-items: flex-start;
    margin-bottom: 15px;
  }

  .profile-icon {
    flex-shrink: 0;
    width: 60px;
    height: 60px;
    background-color: #EFC1C1;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 28px;
    color: #3B435F;
    margin-right: 20px;
    font-weight: bold;
  }

  .name-info {
    flex-grow: 1;
  }

  .full-name {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 5px;
    color: #000;
  }

  .phone {
    font-size: 15px;
    color: #292F33;
  }

  .edit-name-button {
    background-color: #4833DA;
    color: white;
    border: none;
    padding: 8px 15px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .edit-name-button:hover {
    background-color: #3a29b0;
  }

  .pickup-point-name {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 5px;
  }

  .pickup-point-address {
    font-size: 16px;
    color: #666;
    margin-bottom: 15px;
  }

  .change-pickup-button {
    background-color: #4833DA;
    color: white;
    border: none;
    padding: 8px 15px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .change-pickup-button:hover {
    background-color: #3a29b0;
  }

  .logout-button {
    background-color: #DD2E44;
    color: white;
    border: none;
    padding: 15px;
    border-radius: 8px;
    font-size: 18px;
    font-weight: 500;
    cursor: pointer;
    width: 100%;
    transition: background-color 0.2s;
    margin-bottom: 30px;
  }

  .logout-button:hover {
    background-color: #c62a3e;
  }

  .compliance-links {
    display: flex;
    flex-direction: column;
    margin-top: 30px;
  }

  .compliance-link {
    color: #4833DA;
    text-decoration: none;
    font-size: 16px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
  }

  .compliance-link::after {
    content: '↗';
    margin-left: 5px;
    font-size: 18px;
  }

  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-end;
    z-index: 1000;
  }

  .popup {
    background-color: white;
    padding: 30px;
    border-radius: 20px 20px 0 0;
    width: 100%;
    height: 70vh;
    overflow-y: auto;
    position: relative;
    animation: slideUp 0.3s ease-out;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  .popup h2 {
    font-size: 24px;
    margin-bottom: 20px;
  }

  .popup input {
    width: 100%;
    padding: 12px;
    margin-bottom: 20px;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 16px;
  }

  .popup button {
    background-color: #4833DA;
    color: white;
    border: none;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    margin-right: 10px;
    transition: background-color 0.2s;
  }

  .popup button:hover {
    background-color: #3a29b0;
  }

  .update-message {
    color: #4CAF50;
    margin-top: 10px;
    font-size: 16px;
  }

  .error-message {
    color: #DD2E44;
    margin-top: 10px;
    font-size: 16px;
  }

  .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
  }

  .dot-loader {
    display: flex;
  }

  .dot {
    width: 12px;
    height: 12px;
    background-color: #4833DA;
    border-radius: 50%;
    margin: 0 5px;
    animation: bounce 0.5s infinite alternate;
  }

  .dot:nth-child(2) {
    animation-delay: 0.1s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.2s;
  }

  @keyframes bounce {
    to {
      transform: translateY(-10px);
    }
  }

  .submit-button-content {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .submit-loader {
    width: 20px;
    height: 20px;
    border: 2px solid #ffffff;
    border-top: 2px solid transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-left: 10px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .close-button {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #333;
  }

  .about-us-content {
    font-size: 16px;
    line-height: 1.6;
    color: #333;
  }

  .about-us-content h3 {
    font-size: 20px;
    margin-top: 20px;
    margin-bottom: 10px;
    color: #4833DA;
  }

  .about-us-content p {
    margin-bottom: 15px;
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #777;
    font-size: 0.8rem;
    transition: color 0.3s ease;
  }

  .nav-item.active {
    color: #DD2E44;
  }

  .nav-icon {
    font-size: 1.5rem;
    margin-bottom: 2px;
  }

  .nav-item.active .nav-icon {
    color: #DD2E44;
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 50vh;
    text-align: center;
    padding: 0 20px;
  }

  .error-message {
    color: #721c24;
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    padding: 15px;
    margin-bottom: 20px;
  }

  .refresh-button {
    background-color: #4833DA;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .refresh-button:hover {
    background-color: #3a29b0;
  }
  
  /* Support section styles */
  .section-title {
    font-size: 18px;
    font-weight: 600;
    margin: 25px 0 15px;
    color: #333;
  }
  
  .support-card {
    background-color: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 15px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  .support-item {
    display: flex;
    align-items: center;
    padding: 15px 0;
    border-bottom: 1px solid #eee;
  }
  
  .support-item:last-child {
    border-bottom: none;
  }
  
  .support-icon {
    width: 45px;
    height: 45px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    margin-right: 15px;
    flex-shrink: 0;
  }
  
  .whatsapp-icon {
    background-color: #25D366;
    color: white;
  }
  
  .group-icon {
    background-color: #128C7E;
    color: white;
  }

  .phone-icon {
    background-color: #007BFF;
    color: white;
  }
  
  .support-content {
    flex: 1;
  }
  
  .support-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 5px;
    color: #333;
  }
  
  .support-description {
    font-size: 14px;
    color: #666;
    margin: 0;
  }
  
  .support-button {
    display: inline-block;
    background-color: #25D366;
    color: white;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 8px;
    margin-top: 12px;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .support-button.support-primary {
    background-color: #25D366;
  }

  .support-button.support-primary:active {
    background-color: #3a29b0;
  }
  
  .support-button:active {
    background-color: #1fa855;
  }

  .phone-number {
    font-size: 16px;
    font-weight: 600;
    color: #007BFF;
    margin-top: 5px;
  }
</style>

<div class="profile-view" in:fly="{{ y: 20, duration: 300 }}">
  <div class="header">
    <button class="back-button" on:click={goBack}>
      <img src={backarrow} alt="Back" /> 
    </button>
    <h1 class="header-title">Profile</h1>
  </div>

  {#if isLoading}
    <div class="loader">
      <div class="dot-loader">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <button class="refresh-button" on:click={handleRefresh}>
        Refresh
      </button>
    </div>
  {:else if profile}
    <div class="card">
      <div class="profile-info">
        <div class="profile-icon">{profile.full_name ? profile.full_name[0].toUpperCase() : 'U'}</div>
        <div class="name-info">
          <div class="full-name">{profile.full_name || 'N/A'}</div>
          <div class="phone">{profile.phone || 'N/A'}</div>
        </div>
      </div>
      <button class="edit-name-button" on:click={openEditNamePopup}>Edit Name</button>
    </div>

    <div class="card">
      <div class="pickup-point-name">{profile.delivery_point?.name || 'N/A'}</div>
      <div class="pickup-point-address">{profile.delivery_point?.address || 'N/A'}</div>
      <button class="change-pickup-button" on:click={changePickupPoint}>Change pickup point</button>
    </div>
    
    <!-- Support Section -->
    <h2 class="section-title">Support & Updates</h2>
    
    <div class="support-card">
      <div class="support-item" on:click={viewSupport}>
        <div class="support-icon whatsapp-icon">💬</div>
        <div class="support-content">
          <div class="support-title">Message us</div>
          <div class="support-description">Message us for support/feedback</div>
          <button class="support-button support-primary">Message</button>
        </div>
      </div>
      
      <div class="support-item" on:click={joinWhatsAppGroup}>
        <div class="support-icon group-icon">👥</div>
        <div class="support-content">
          <div class="support-title">Join our WhatsApp Group</div>
          <div class="support-description">Get regular updates on our products</div>
          <button class="support-button">Join Group</button>
        </div>
      </div>
    </div>

    <!-- Contact Information -->
    <div class="support-card">
      <div class="support-item" on:click={chatWithSupport}>
        <div class="support-icon phone-icon">📞</div>
        <div class="support-content">
          <div class="support-title">Contact Phone</div>
          <div class="support-description">Call us for urgent support</div>
          <div class="phone-number">9490028494</div>
        </div>
      </div>
    </div>

    <button class="logout-button" on:click={logout}>Logout</button>

    <div class="compliance-links">
      <a href="#" class="compliance-link" on:click|preventDefault={openAboutUsPopup}>About Us</a>
      <a href="#" class="compliance-link" on:click|preventDefault={openTermsPopup}>Terms of service</a>
      <a href="#" class="compliance-link" on:click|preventDefault={openPrivacyPopup}>Privacy policy</a>
    </div>
  {:else}
    <div class="error-container">
      <p class="error-message">No profile data available.</p>
      <button class="refresh-button" on:click={handleRefresh}>
        Refresh
      </button>
    </div>
  {/if}
</div>

{#if showEditNamePopup}
  <div class="popup-overlay" on:click={closeEditNamePopup}>
    <div class="popup" on:click|stopPropagation>
      <h2>Edit Name</h2>
      <input type="text" bind:value={newName} placeholder="Enter new name">
      <button on:click={updateName} disabled={isSubmitting}>
        <div class="submit-button-content">
          Submit
          {#if isSubmitting}
            <div class="submit-loader"></div>
          {/if}
        </div>
      </button>
      <button on:click={closeEditNamePopup}>Cancel</button>
      {#if updateMessage}
        <div class={updateMessage.includes('Error') || updateMessage.includes('Failed') ? 'error-message' : 'update-message'}>
          {updateMessage}
        </div>
      {/if}
    </div>
  </div>
{/if}



{#if showAboutUsPopup}
  <div class="popup-overlay" on:click={closeAboutUsPopup}>
    <div class="popup" on:click|stopPropagation>
      <button class="close-button" on:click={closeAboutUsPopup}>×</button>
      <h2>About Us</h2>
      <div class="about-us-content">
        <p>Group Fruits is a pioneering grocery pickup service based in Eluru, Andhra Pradesh, India. Our mission is to provide the freshest and most affordable fruits and grocery to our valued customers. By leveraging a unique business model, we offer products at direct wholesale prices, bringing otherwise unavailable items to our consumers through group orders.</p>
        
        <h3>Our Concept</h3>
        <p>We focus primarily on fruits, aiming to deliver them at the lowest possible prices. To achieve this, we maintain several pickup points across Eluru where customers can collect their orders, eliminating the need for delivery services and reducing costs. For common fruits, the pickup timeline is usually the next day. However, for special items brought in on pre-order from distant locations, the pickup time is typically 3-4 days.</p>
        
        <h3>Our Commitment</h3>
        <p>At Group Fruits, we are committed to quality and transparency. We prioritize bringing organic fruits to our customers whenever available. As a policy, we never sell fruits that are ripened using artificial chemicals like carbide or chemical dips. Additionally, we do not offer manufactured food items containing artificial colorants or preservatives.</p>
        
        <p>Our goal is to make healthy and fresh fruits accessible to everyone in Eluru, ensuring that our community can enjoy high-quality produce at unbeatable prices. Join us in our mission to promote healthy living and support sustainable practices.</p>
        
        <p>Thank you for choosing Group Fruits!</p>
      </div>
    </div>
  </div>
{/if}






{#if showTermsPopup}
  <div class="popup-overlay" on:click={closeTermsPopup}>
    <div class="popup" on:click|stopPropagation>
      <button class="close-button" on:click={closeTermsPopup}>×</button>
      <h2>Terms Of Service</h2>
      <div class="terms-of-service-content">
        <h3>1. Acceptance of Terms</h3>
        <p>By accessing and using our website, you accept and agree to be bound by the terms and provisions of this agreement. This agreement constitutes a legally binding contract between you and the Company, and it governs your use of the Company's services. By using our services, you represent and warrant that you have read, understood, and agreed to be bound by this agreement. If you do not agree with any of the terms and conditions set forth in this agreement, please do not use our website or any of its services.</p>

        <h3>2. Description of Service</h3>
        <p>We provide an online marketplace for grocery products, operating on both a marketplace and inventory model. Our platform connects users with a wide range of products from various categories, including fresh produce, pantry essentials, personal care items, and more. While we strive to offer a diverse selection of products, we want to clarify that we are not responsible for the warranty, additional charges, or any other obligations related to the marketplace products.</p>
        <p>As we operate on a marketplace model, we act as an intermediary between buyers and sellers, providing a platform for third-party sellers to list and sell their products. While we make every effort to ensure that the products listed by our sellers are genuine and of good quality, we cannot guarantee the accuracy, completeness, or reliability of any product information provided by third-party sellers.</p>

        <h3>3. Membership</h3>
        <p>To access certain features of our website, users may be required to register for an account and become members of our platform. We reserve the right to introduce additional charges, such as a service membership fee, in the future. Users who are not members or have not paid the required membership fees may not be eligible for our services, including the delivery of goods.</p>
        <p>By registering for an account, you agree to provide accurate, current, and complete information about yourself as prompted by the registration form. You are responsible for maintaining the confidentiality of your account password and for any activities or actions that occur under your account. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.</p>

        <h3>4. Service Availability</h3>
        <p>Our services are currently restricted to specific cities and areas. We are continuously working to expand our serviceable areas, but we are not liable to deliver goods to locations outside our current coverage. The pickup timing for goods depends on the specific pickup point location timelines, and we encourage our users to check the available pickup slots during the order placement process.</p>
        <p>We do our best to provide accurate information regarding the availability of our services in various locations. However, we cannot guarantee that our services will be available in all areas at all times. We reserve the right to modify, suspend, or discontinue our services, or any part thereof, at any time, with or without notice.</p>

        <h3>5. Collection of Perishable Goods</h3>
        <p>Users are required to collect perishable goods, such as fruits, vegetables, dairy products, and other items with a short shelf life, on the same day of delivery. We are not responsible for any decline in quality if goods are not collected promptly. To ensure the freshness and quality of the products, it is essential that users pick up their orders within the designated time frame.</p>
        <p>In case you are unable to collect your order on the scheduled day, please contact our customer support team as soon as possible. We will do our best to accommodate your request, but we cannot guarantee that we will be able to reschedule the delivery or hold the order for an extended period.</p>

        <h3>6. Disposal of Uncollected Goods</h3>
        <p>We reserve the right to dispose of goods that are not collected and show signs of spoilage. This measure is taken to avoid contamination of the pickup point store and potential food safety issues. By placing an order for perishable goods, you acknowledge and agree that we may dispose of your order if it is not collected within the designated time frame.</p>

        <h3>7. Eligibility</h3>
        <p>Persons who are "incompetent to contract" within the meaning of the Indian Contract Act, 1872, including minors, undischarged insolvents, and persons of unsound mind, are not eligible to use/access the Marketplace. By using our services, you represent and warrant that you are of legal age and have the capacity to enter into a binding contract.</p>

        <h3>8. Registration</h3>
        <p>All users must register and log in to place orders on the Marketplace. You must keep your account and registration details current and correct for all communications related to your purchases from the Marketplace. By agreeing to the Terms of Use, you agree to receive promotional or transactional communication and newsletters from the Company and its partners. You can opt out from such communication and/or newsletters either by contacting the customer services team of Marketplace or by placing a request for the same through the website or mobile application.</p>

        <h3>9. Pricing</h3>
        <p>While we aim to ensure that prices of all products offered for sale are true and correct, from time to time, the prices of certain products may not be current or may be inaccurate due to technical issues, typographical errors, or incorrect product information provided to us by third-party sellers. In such cases, we reserve the right to cancel the order without any further liability. If we discover an error in the pricing of a product that you have ordered, we will inform you as soon as possible and give you the option to either reconfirm your order at the correct price or cancel it.</p>

        <h3>10. Order Cancellation</h3>
        <p>Orders once placed may or may not be cancelled, depending on the item type. For highly sensitive and perishable goods, we may not be able to offer cancellation. For some items, cancellation is available until the goods are packed in our warehouse. If you wish to cancel an order, please contact our customer support team immediately, and we will do our best to accommodate your request.</p>

        <h3>11. Order Modification and Delivery</h3>
        <p>Due to unforeseen circumstances, we may have to partially or fully cancel an order or postpone the delivery to the next available date. This is particularly applicable to fresh fruits and vegetables, where supply can be unpredictable. We will make every effort to inform you of any changes to your order as soon as possible.</p>

        <h3>12. Returns and Refunds</h3>
        <p>As we deal mostly with food products, we do not accept returns due to food safety risks associated with the resale of food already collected or opened by the consumer. However, we understand that there may be instances where a product is damaged, defective, or expired upon delivery. In such cases, we may offer a refund or replacement, subject to a full review of the case.</p>
        <p>For all food items, complaints regarding damages, defects, or expiration must be communicated within 2 days of delivery, and for sensitive and perishable items, complaints should be communicated on the same day as pickup or the next day. To initiate a refund or replacement request, please contact our customer support team and provide them with the necessary details and evidence, such as photographs or videos of the product.</p>
        <p>Once we have received and reviewed your request, we will inform you of our decision within a reasonable time frame. If your request is approved, the refund will be processed to the original payment instrument used for the purchase. In cases where a refund to the original payment instrument is not possible, we may refund the amount to your in-app wallet or through other offline/external methods.</p>

        <h3>13. Taste Complaints</h3>
        <p>As the taste of a product is subjective, we are unable to offer any refunds based on taste complaints. We strive to provide accurate descriptions and images of our products, but it is important to note that taste preferences can vary greatly from person to person. If you have any concerns about the taste of a product, we encourage you to contact our customer support team, and we will do our best to address your concerns.</p>

        <h3>14. Order Cancellation by Company</h3>
        <p>We reserve the right to cancel any order at any time for any user. Cases such as fraudulent transactions, suspected fraudulent users, misuse of promotions, or violation of our terms and conditions can lead to the cancellation of an order and further actions. If we cancel an order due to any of the aforementioned reasons, we will notify you as soon as possible and provide a refund for any payments made towards the cancelled order.</p>

        <h3>15. Intellectual Property</h3>
        <p>All content, trademarks, and data on our website, including but not limited to text, graphics, logos, button icons, images, audio clips, digital downloads, data compilations, and software, are the property of the Company or its content suppliers and are protected by Indian and international copyright, trademark, and other intellectual property laws. The compilation of all content on this website is the exclusive property of the Company and is protected by Indian and international copyright laws.</p>

        <h3>16. Governing Law and Jurisdiction</h3>
        <p>This agreement shall be governed by and construed in accordance with the laws of India. Any dispute arising out of or in connection with this agreement shall be subject to the exclusive jurisdiction of the courts in the city of Eluru, Andhrapradesh, India.</p>

        <h3>17. Entire Agreement</h3>
        <p>This agreement constitutes the entire agreement between you and the Company with respect to the use of our website and services, superseding any prior agreements or understandings, whether written or oral.</p>

        <h3>18. Amendments</h3>
        <p>The Company reserves the right to modify or amend this agreement at any time without prior notice. Any changes to this agreement will be posted on our website, and it is your responsibility to review the terms and conditions regularly. Your continued use of our website and services after any amendments to this agreement will constitute your acceptance of such amendments.</p>

        <p>If you have any questions or concerns regarding our terms of service, please contact us at 9490028494, and we will be happy to assist you.</p>
      </div>
    </div>
  </div>
{/if}






{#if showPrivacyPopup}
  <div class="popup-overlay" on:click={closePrivacyPopup}>
    <div class="popup" on:click|stopPropagation>
      <button class="close-button" on:click={closePrivacyPopup}>×</button>
      <h2>Privacy Policy</h2>
      <div class="privacy-policy-content">
        <p>Group Fruits is committed to protecting the privacy and security of our customers. This Privacy Policy explains how we collect, use, share, and protect your personal information in accordance with applicable privacy laws and regulations. By using our services, you consent to the practices described in this Privacy Policy.</p>

        <h3>1. Information Collection</h3>
        <p>We collect various types of information from and about our users, including:</p>
        <ul>
          <li>a. Personal Information: We may collect personal information such as your name, email address, phone number, postal address, and payment information when you create an account, place an order, or communicate with us.</li>
          <li>b. Usage and Device Information: We may automatically collect information about your use of our services, including your IP address, browser type, device type, operating system, and browsing behavior.</li>
          <li>c. Location Information: We may collect your location information when you use our services, such as when you search for pickup points or place an order for delivery.</li>
          <li>d. Third-Party Information: We may receive information about you from third-party sources, such as social media platforms, public databases, and our business partners.</li>
        </ul>

        <h3>2. Information Use</h3>
        <p>We use the information we collect for various purposes, including:</p>
        <ul>
          <li>a. Providing and Improving Our Services: We use your information to process and fulfill your orders, communicate with you about your orders and our services, and improve our offerings.</li>
          <li>b. Personalization: We may use your information to personalize your experience on our platform, such as by recommending products and offering promotions tailored to your interests.</li>
          <li>c. Marketing and Advertising: We may use your information to send you marketing communications, promotional offers, and targeted advertisements.</li>
          <li>d. Analytics and Research: We may use your information to analyze trends, track user engagement, and conduct research to improve our services.</li>
          <li>e. Compliance and Protection: We may use your information to comply with applicable laws, regulations, and legal processes, as well as to protect our rights, property, and safety, and the rights, property, and safety of our users and third parties.</li>
        </ul>

        <h3>3. Information Sharing</h3>
        <p>We may share your information with the following parties:</p>
        <ul>
          <li>a. Service Providers: We may share your information with third-party service providers that help us operate and improve our services, such as payment processors, order fulfillment partners, and analytics providers.</li>
          <li>b. Business Partners: We may share your information with our business partners for marketing, advertising, and other purposes.</li>
          <li>c. Affiliates: We may share your information with our affiliates for internal business purposes.</li>
          <li>d. Legal and Regulatory Authorities: We may share your information with law enforcement, government agencies, and regulatory authorities as required by law or to protect our rights, property, and safety, and the rights, property, and safety of our users and third parties.</li>
          <li>e. Successors: We may share your information in connection with a merger, acquisition, or sale of all or a portion of our assets.</li>
        </ul>

        <h3>4. Information Security</h3>
        <p>We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no security measures can guarantee absolute security, and we cannot ensure or warrant the security of your information.</p>

        <h3>5. Data Retention</h3>
        <p>We retain your information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.</p>

        <h3>6. Your Rights and Choices</h3>
        <p>You may have certain rights and choices regarding the collection, use, and sharing of your information, including:</p>
        <ul>
          <li>a. Access and Update: You may access and update certain personal information by logging into your account or contacting us.</li>
          <li>b. Marketing Communications: You may opt out of receiving marketing communications from us by following the unsubscribe instructions in our emails or contacting us.</li>
          <li>c. Cookies and Tracking Technologies: You may manage your preferences for cookies and other tracking technologies through your browser settings or by using tools such as ad blockers.</li>
          <li>d. Legal Rights: Depending on your jurisdiction, you may have additional rights under applicable privacy laws, such as the right to request deletion, restriction, or portability of your information.</li>
        </ul>

        <h3>7. International Transfers</h3>
        <p>We may transfer your information to countries other than the country in which the information was originally collected. These countries may not have the same data protection laws as the country in which you initially provided the information. When we transfer your information to other countries, we will protect it as described in this Privacy Policy.</p>

        <h3>8. Third-Party Websites and Services</h3>
        <p>Our platform may contain links to third-party websites or services that are not owned or controlled by us. We are not responsible for the privacy practices of these third parties, and we encourage you to review their privacy policies before providing any information to them.</p>

        <h3>9. Children's Privacy</h3>
        <p>Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under the age of 18. If we become aware that we have collected personal information from a child under the age of 18, we will take steps to delete such information.</p>

        <h3>10. Changes to This Privacy Policy</h3>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated Privacy Policy on our platform and revising the "Last Updated" date above. Your continued use of our services after the effective date of the revised Privacy Policy constitutes your acceptance of the updated policy.</p>

        <p>If you have any questions or concerns regarding this Privacy Policy or our privacy practices, please contact us at:</p>
        <p>Group Fruits<br>
        12-87-Hb,<br>
        Kandrikagudem Center,<br>
        Eluru.<br>
        Mob: 9490028494</p>

        <p>Thank you for choosing Group Fruits!</p>
      </div>
    </div>
  </div>
{/if}
	  
	  
  
<div class="bottom-nav">
  <div 
    class="nav-item {activeTab === 'home' ? 'active' : ''}"
    on:click={() => {
      activeTab = 'home';
      dispatch('goToHome');
    }}
  >
    <span class="nav-icon">🏠</span>
    <span>Home</span>
  </div>
  <div 
    class="nav-item {activeTab === 'orders' ? 'active' : ''}"
    on:click={() => dispatch('pleaseOrderHistory')}
  >
    <span class="nav-icon">📦</span>
    <span>Order History</span>
  </div>
  <div 
    class="nav-item {activeTab === 'settings' ? 'active' : ''}"
    on:click={() => {
      activeTab = 'settings';
      dispatch('profileView');
    }}
  >
    <span class="nav-icon">☰</span>
    <span>Profile</span>
  </div>
</div>
